import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, queryMapFactory } from "../common/reducers/utilities";
import { complexTableQuery } from "../common/utils/tableUtilities";

const participantSlice = createSlice({
    name: 'participants',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload).forEach(participant => state.data[participant.id] = participant);
        },
        addOrUpdate: (state, action) => {
            for (const participant of ensureArray(action.payload)) {
                state.data[participant.id] = participant;
            }
        },
        remove: (state, action) => {
            for (const participant of ensureArray(action.payload)) {
                delete state.data[participant.id];
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => participantSlice.getInitialState());
    }
});

// #region network operations
const fetchParticipant = id => network => network.get('/api/v1/participant/' + encodeURIComponent(id), participantSlice.actions.set);
const queryParticipant = query => network => network.post('/api/v2/participant', query, participantSlice.actions.set);
// #endregion

// #region selector
const selectParticipantData = state => {
    return state.participants.data;
}
const makeSelectParticipant = createSelector([selectParticipantData, (state, query) => query], (data, query) => {
    const matches = complexTableQuery(Object.values(data), { data: query });
    return matches[0]
});
const selectParticipant = query => state => makeSelectParticipant(state, query);
// #endregion

// #region listeners
const listen = {
    participantCreated: (participant, { dispatch }) => dispatch(participantSlice.actions.addOrUpdate(participant)),
    participantUpdated: (participant, { dispatch }) => dispatch(participantSlice.actions.addOrUpdate(participant)),
    participantRemoved: (participant, { dispatch }) => dispatch(participantSlice.actions.remove(participant))
}
// #endregion

const initById = initMapFactory(fetchParticipant, listen);
const options = { cleanup: { root: 'participants', all: state => Object.values(state.participants.data), remove: participantSlice.actions.remove, removeAll: participantSlice.actions.remove } };
export const [useParticipant] = dataHookFactory(selectParticipant, initMapFactory, options);
export const [useParticipantByQuery] = dataHookFactory(selectParticipant, queryMapFactory(queryParticipant, 'id', initById, listen), options);

const reducerDetails = { name: participantSlice.name, reducer: participantSlice.reducer };
export default reducerDetails;